import { Ng2StateDeclaration, Transition, UIRouterModule } from '@uirouter/angular';
import { Component } from '@angular/core';
import { LoginComponent } from '@client/app/pages/login/login/login.component';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { StateService, UrlService } from '@uirouter/core';
import { OauthService } from '@monsido/oauth/oauth.service';
import { ErrorHandler } from '@client/app/core/services/error-handler/error-handler';
import { LoginService } from '@client/app/core/services/login/login.service';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'base-login',
    template: '<div class="container-fluid" ui-view></div>',
    standalone: true,
    imports: [
        UIRouterModule,
    ],
})
class BaseLoginComponent {}

export const LOGIN_STATES: Ng2StateDeclaration[] = [
    {
        name: 'login',
        url: '',
        component: BaseLoginComponent,
        data: { anonymous_access: true, title: 'Acquia Web Governance' },
    },
    {
        name: 'login.login',
        url: '/login',
        component: LoginComponent,
        data: { anonymous_access: true, title: 'Acquia Web Governance' },
    },
    {
        name: 'login.auth',
        url: '/auth?code&state&sudo',
        data: { anonymous_access: true, title: 'Acquia Web Governance' },
        params: {
            code: { dynamic: true },
            state: { dynamic: true },
            sudo: { dynamic: true },
        },
        onExit: function (transition) {
            const ng2MonEventsService = transition.injector().get(MonEventService);
            return ng2MonEventsService.run('exitLogin');
        },
        onEnter: async function (transition: Transition) {
            const injector = transition.injector();
            const urlService = injector.get(UrlService);
            const oauthService = injector.get(OauthService);
            const ng2ErrorHandler = injector.get(ErrorHandler);
            const $state = injector.get(StateService);
            const ng2MonEventsService = injector.get(MonEventService);
            const ng2CoreLoginService = injector.get(LoginService);

            if (!urlService.search().hasOwnProperty('code')) {
                await ng2MonEventsService.run('afterLogin');
                await ng2MonEventsService.run('finishLogin');
                return;
            }

            if (urlService.search().sudo) {
                ng2CoreLoginService.setSudoSession();
            }

            await ng2MonEventsService.run('beforeLogin');

            try {
                await firstValueFrom(oauthService.getAccessToken(urlService.search().code));
            } catch (error) {
                ng2ErrorHandler.noopError(error, '');
                await ng2MonEventsService.run('force logout').catch(() => {});
                transition.promise.then(() => $state.go('login.login'));
                return;
            }

            if (urlService.search().hasOwnProperty('logout_url')) {
                oauthService.setLogoutUrl(decodeURIComponent(urlService.search().logout_url));
                const url = new URL(window.location.href);
                url.searchParams.delete('logout_url');
                window.history.replaceState({}, '', url.toString());
            }

            if (oauthService.getState()) {
                const state = oauthService.getState();
                const params = oauthService.getStateParams();
                oauthService.clearParams();
                transition.promise.then(() => {
                    return $state.go(state, params).then(() => {
                        const {
                            issueOverlay,
                            issueOverlayIssueId,
                            issueOverlayIssueType,
                            issueOverlaySourceCodeId,
                            issueOverlayContentId,
                            issueOverlayPageId,
                            issueOverlayCheckId,
                            issueOverlayPageLinkId,
                        } = params;

                        if (issueOverlay && issueOverlayIssueId && issueOverlayIssueType) {
                            const url = new URL(window.location.href);
                            url.searchParams.set('issueOverlay', issueOverlay);
                            url.searchParams.set('issueOverlayIssueId', issueOverlayIssueId);
                            url.searchParams.set('issueOverlayIssueType', issueOverlayIssueType);
                            url.searchParams.set('issueOverlaySourceCodeId', issueOverlaySourceCodeId);
                            url.searchParams.set('issueOverlayContentId', issueOverlayContentId);
                            url.searchParams.set('issueOverlayPageId', issueOverlayPageId);
                            url.searchParams.set('issueOverlayCheckId', issueOverlayCheckId);
                            url.searchParams.set('issueOverlayPageLinkId', issueOverlayPageLinkId);
                            window.history.replaceState({}, '', url.toString());
                        }
                    });
                });
            } else if (urlService.search().customer_id) {
                transition.promise.then(() => {
                    $state.go('base.customer.dashboard', {
                        customerId: urlService.search().customer_id,
                    });
                });
            } else {
                transition.promise.then(() => {
                    $state.go('base.dashboard');
                });
            }

            await ng2MonEventsService.run('afterLogin');
            await ng2MonEventsService.run('finishLogin');
        },
    },
];
